// Project pulse — pure aggregation behind the field dashboard's hero chart.
// No DOM, no Supabase: rows in, {mode, bars} out. RLS shapes the input
// (a foreman's rows are only their own; an org admin gets the whole
// project), so the same math renders "my production" or "the project's
// production" without caring which.

// Monday-start week key (YYYY-MM-DD) for a local date or timestamp string.
// Parses as local-time parts — never Date('YYYY-MM-DD') which is UTC and
// rolls the day over in the afternoon for Pacific users.
export function weekKeyFor(dateStr) {
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  const dow = date.getDay(); // 0=Sun
  date.setDate(date.getDate() + (dow === 0 ? -6 : 1 - dow));
  return _iso(date);
}

// n Monday keys, oldest first, ending at the week containing todayIso.
export function weekBuckets(todayIso, n = 8) {
  const [y, m, d] = weekKeyFor(todayIso).split('-').map(Number);
  const out = [];
  for (let i = n - 1; i >= 0; i--) out.push(_iso(new Date(y, m - 1, d - 7 * i)));
  return out;
}

// rows: flattened production_line_items — [{report_date, material, unit,
// quantity}], one row per report/material (the relational detail
// syncProductionReportToDb writes; the TM history import filled it too, so
// charts carry real history from day one). One series per material, sorted
// by window volume; past maxSeries the tail folds into "Other" so the
// chart never grows an unreadable spaghetti of lines. Units stay per
// series (count vs sf vs lf) — series share the plot to compare PACE, and
// every value is labeled with its own unit, so no shared axis lies.
export function buildMaterialPulse(rows, buckets, maxSeries = 4) {
  const byMat = new Map();   // name → { name, unit, total, byWeek }
  for (const r of rows || []) {
    const w = weekKeyFor(r.report_date);
    const q = Number(r.quantity) || 0;
    if (!buckets.includes(w) || !q) continue;
    const name = r.material || 'Other';
    let m = byMat.get(name);
    if (!m) {
      m = { name, unit: r.unit || '', total: 0, byWeek: Object.fromEntries(buckets.map(k => [k, 0])) };
      byMat.set(name, m);
    }
    m.byWeek[w] += q; m.total += q;
  }
  let series = [...byMat.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
  if (!series.length) return { mode: 'empty', series: [] };
  if (series.length > maxSeries) {
    const keep = series.slice(0, maxSeries - 1);
    const rest = series.slice(maxSeries - 1);
    const other = { name: 'Other', unit: '', total: 0, byWeek: Object.fromEntries(buckets.map(k => [k, 0])) };
    rest.forEach(m => { other.total += m.total; buckets.forEach(k => { other.byWeek[k] += m.byWeek[k]; }); });
    series = [...keep, other];
  }
  return {
    mode: 'production',
    weeks: buckets.slice(),
    series: series.map(m => ({ name: m.name, unit: m.unit, total: m.total, values: buckets.map(k => m.byWeek[k]) })),
  };
}

function _iso(d) {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') +
         '-' + String(d.getDate()).padStart(2, '0');
}
